import { Lib } from '@/lib'
import { isApp } from '@/index'
import type { CodepushConfig, UpdateResult, UpdateEvent } from './type'
import { PublicAPI } from '@/app/data/api'
import type { AppVersion } from '@/app/data/type'
import { CloudFile } from '@/lib/CloudFile'

export class Codepush {
  private config: CodepushConfig
  private eventListeners: Map<string, Array<(event: UpdateEvent) => void>> = new Map()
  private checkTimer?: ReturnType<typeof setTimeout>
  private isUpdating = false

  constructor(config: CodepushConfig) {
    this.config = config
  }

  static async notifyAppReady(): Promise<void> {
    try {
      const { Capacitor } = await import('@capacitor/core')
      if (Capacitor.isNativePlatform()) {
        const { CapacitorUpdater } = await import('@capgo/capacitor-updater')
        await CapacitorUpdater.notifyAppReady()
        console.info('[codepush] notifyAppReady called immediately on a native platform.')
      }
    } catch {
      // 에러 무시
    }
  }

  // 이벤트 리스너 등록
  on(eventType: UpdateEvent['type'], callback: (event: UpdateEvent) => void): void {
    if (!this.eventListeners.has(eventType)) this.eventListeners.set(eventType, [])
    this.eventListeners.get(eventType)!.push(callback)
  }

  // 이벤트 리스너 제거
  off(eventType: UpdateEvent['type'], callback: (event: UpdateEvent) => void): void {
    const listeners = this.eventListeners.get(eventType)
    if (listeners) {
      const index = listeners.indexOf(callback)
      if (index > -1) listeners.splice(index, 1)
    }
  }

  // 이벤트 발생
  private emit(eventType: UpdateEvent['type'], data?: unknown): void {
    const event: UpdateEvent = { type: eventType, data }
    const listeners = this.eventListeners.get(eventType)
    if (listeners)
      listeners.forEach((callback: (event: UpdateEvent) => void) => {
        callback(event)
      })
  }

  // 초기화
  async initialize(): Promise<void> {
    console.info('[codepush] initialize')
    if (!isApp()) return

    // 앱 시작 시 업데이트 확인 및 준비 완료 알림
    await this.handleAppReady()

    // checkInterval이 0보다 크면 자동 체크 활성화
    if (this.config.checkInterval && this.config.checkInterval > 0) this.startAutoCheck()
  }

  // 앱 준비 완료 처리
  private async handleAppReady(): Promise<void> {
    try {
      const { CapacitorUpdater } = await import('@capgo/capacitor-updater')

      // 리로드 플래그 확인
      const reloadFlag = localStorage.getItem('codepush_reload_flag')
      if (reloadFlag === 'true') {
        // 현재 번들 정보 확인
        const current = await CapacitorUpdater.current()
        console.info(
          '[codepush] App reloaded after update. Current bundle:',
          JSON.stringify(current),
        )

        // 이전 번들들 정리 (백그라운드에서 실행, 앱 시작을 막지 않음)
        this.cleanupOldBundles()
        this.emit('reloaded')
      }
    } catch (error) {
      console.error('[codepush] handleAppReady error:', error)
    }
  }

  // 이전 번들들 정리
  private async cleanupOldBundles(): Promise<void> {
    try {
      const { CapacitorUpdater } = await import('@capgo/capacitor-updater')

      const bundlesList = await CapacitorUpdater.list()
      console.info('[codepush] available bundles:', JSON.stringify(bundlesList))

      // 현재 번들 제외하고 이전 번들들 삭제
      const current = await CapacitorUpdater.current()
      const currentBundleId = current?.bundle?.id
      if (!currentBundleId) return

      for (const bundle of bundlesList.bundles) {
        if (bundle.id !== currentBundleId && bundle.id !== 'builtin') {
          try {
            await CapacitorUpdater.delete({ id: bundle.id })
            console.info('[codepush] deleted old bundle:', bundle.id)
          } catch (error) {
            console.warn('[codepush] failed to delete bundle:', bundle.id, error)
          }
        }
      }
    } catch (error) {
      console.error('[codepush] cleanupOldBundles error:', error)
    }
  }

  // 자동 업데이트 체크
  private startAutoCheck(): void {
    if (this.checkTimer) clearInterval(this.checkTimer)
    const interval = (this.config.checkInterval || 30) * 60 * 1000
    this.checkTimer = setInterval(async () => {
      if (!this.isUpdating) await this.checkForUpdates()
    }, interval)
  }

  // 자동 업데이트 체크 중지
  stopAutoCheck(): void {
    if (this.checkTimer) {
      clearInterval(this.checkTimer)
      this.checkTimer = undefined
    }
  }

  // 업데이트 체크
  async checkForUpdates(): Promise<void> {
    console.info('[codepush] checkForUpdates')
    this.emit('checking')

    try {
      let availableVersions: AppVersion[] = []
      try {
        availableVersions = await PublicAPI.getPublicAppVersions(Lib.webVersion)
      } catch {
        // api 에러 무시
      }
      console.info('[codepush] availableVersions', availableVersions)

      let needsWebAssetUpdate = false

      if (availableVersions.length > 0) {
        // force_update가 1인 버전 중 가장 최신인 행 업데이트 (강제 업데이트)
        const forceUpdateVersion = availableVersions.find((row: AppVersion) =>
          Boolean(row.force_update),
        )
        if (forceUpdateVersion) {
          needsWebAssetUpdate = true
          await this.performUpdate(forceUpdateVersion)
        }
      }

      // 웹에셋 업데이트가 필요없을 때만 네이티브 앱 버전 체크
      if (!needsWebAssetUpdate) {
        const { checkRequiredNativeUpdate } = await import('@/app/util')
        await checkRequiredNativeUpdate()
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류'
      this.emit('error', { error: errorMessage })
    }
  }

  // 업데이트
  async performUpdate(updateInfo: AppVersion): Promise<UpdateResult> {
    console.info('[codepush] performUpdate')
    if (this.isUpdating) return { success: false, error: '이미 업데이트가 진행 중입니다.' }

    this.isUpdating = true

    try {
      // SQLite 연결 정리
      const { SQL } = await import('@/lib/Sqlite')
      await SQL.closeConnection()

      // 웹에셋 다운로드
      this.emit('downloading', updateInfo)
      const { CapacitorUpdater } = await import('@capgo/capacitor-updater')

      const downloadUrl = CloudFile.ResourceUrlToSignedUrl(updateInfo.url as `storage://${string}`)
      const downloadResult = await CapacitorUpdater.download({
        url: downloadUrl,
        version: updateInfo.version,
      })

      if (!downloadResult) throw new Error('다운로드 실패')
      console.info('[codepush] download success:', downloadResult)

      // 설치
      this.emit('installing', updateInfo)
      await CapacitorUpdater.set({ id: downloadResult.id })
      console.info('[codepush] bundle set success:', downloadResult.id)

      // 리로드 플래그 설정
      localStorage.setItem('codepush_reload_flag', 'true')
      localStorage.setItem('codepush_target_version', updateInfo.version)

      // 웹뷰 리로드
      await CapacitorUpdater.reload()

      this.emit('success', updateInfo)
      Lib.webVersion = updateInfo.version
      return { success: true, version: updateInfo.version }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류'
      console.error('[codepush] performUpdate error:', error)
      this.emit('error', { error: errorMessage, updateInfo })
      return { success: false, error: errorMessage }
    } finally {
      this.isUpdating = false
    }
  }

  // 코드푸시 시스템 종료
  destroy(): void {
    this.stopAutoCheck()
    this.eventListeners.clear()
  }
}

// 싱글톤 인스턴스
let codepushInstance: Codepush | null = null

export function createCodepush(config: CodepushConfig): Codepush {
  if (codepushInstance) codepushInstance.destroy()
  codepushInstance = new Codepush(config)
  return codepushInstance
}

export function getCodepush(): Codepush | null {
  return codepushInstance
}
